import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import OpportunityCard from './OpportunityCard';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatCount = (count) => {
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}k`;
  }
  return count.toString();
};

const StageColumn = ({ 
  stage, 
  opportunities = [], 
  colorMode = 'colored_dot',
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
    data: {
      type: 'stage',
      stage,
    },
  });

  const opportunityIds = opportunities.map((o) => o.id);
  const totalValue = opportunities.reduce((sum, o) => sum + (o.value || 0), 0);
  const count = opportunities.length;

  return (
    <div
      className={cn(
        'flex-shrink-0 w-[280px] flex flex-col',
        'rounded-lg',
        isOver && 'ring-2 ring-blue-400 ring-opacity-50'
      )}
      data-testid={`stage-column-${stage.id}`}
    >
      {/* Column Header - GHL Style */}
      <div className="bg-white rounded-t-lg border border-slate-200 border-b-0 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Stage name with color mode */}
            {colorMode === 'colored_dot' && stage.color && (
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: stage.color }}
              />
            )}
            
            <h3 
              className={cn(
                'font-semibold text-sm text-stone-800 truncate',
                colorMode === 'background_tint' && stage.color && 'px-2 py-0.5 rounded'
              )}
              style={colorMode === 'background_tint' && stage.color ? {
                backgroundColor: `${stage.color}20`,
              } : {}}
            >
              {stage.name}
            </h3>
          </div>
          
          {/* Collapse button */}
          <button
            onClick={() => onToggleCollapse?.(stage.id)}
            className="p-1 hover:bg-slate-100 rounded transition-colors"
          >
            <ChevronLeft className={cn(
              'h-4 w-4 text-slate-400 transition-transform',
              isCollapsed && 'rotate-180'
            )} />
          </button>
        </div>
        
        {/* Stats row - GHL style */}
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
          <span>{formatCount(count)} Opportunities</span>
          <span className="font-semibold text-stone-700">{formatCurrency(totalValue)}</span>
        </div>
      </div>

      {/* Cards container */}
      {!isCollapsed && (
        <div
          ref={setNodeRef}
          className={cn(
            'flex-1 p-2 space-y-2 overflow-y-auto bg-slate-50/80',
            'border border-slate-200 border-t-0 rounded-b-lg',
            'min-h-[120px] max-h-[calc(100vh-260px)]',
            isOver && 'bg-blue-50/50'
          )}
        >
          <SortableContext items={opportunityIds} strategy={verticalListSortingStrategy}>
            {opportunities.length === 0 ? (
              <div className="flex items-center justify-center h-20 text-xs text-slate-400 border-2 border-dashed border-slate-200 rounded-lg bg-white/50">
                Drop opportunities here
              </div>
            ) : (
              opportunities.map((opportunity) => (
                <OpportunityCard key={opportunity.id} opportunity={opportunity} />
              ))
            )}
          </SortableContext>
        </div>
      )}
      
      {/* Collapsed state */}
      {isCollapsed && (
        <div className="px-3 py-4 text-center text-xs text-slate-400 border border-slate-200 border-t-0 rounded-b-lg bg-slate-50">
          {count} items
        </div>
      )}
    </div>
  );
};

export default StageColumn;
